import { 
  Play, Users, Clock, AlertTriangle, XCircle, RefreshCw, 
  Shield, Crown, Settings, Layers, Save, CheckCircle, Edit3, Sparkles, X, Plus, Radio, Trash2,
  Search, Coins, Gift, Award
} from 'lucide-react';
import { roomsAPI } from '../utils/api';
import { CountryBadge } from '../components/SearchableCountrySelect';

const LiveSessions = () => {
  const [currentTab, setCurrentTab] = useState('rooms'); // 'rooms', 'level_configs', or 'room_bonuses'
  
  // Room Monitoring State
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('ALL'); // 'ALL' (default so all rooms load immediately) or 'ACTIVE'

  // Room Bonus State (Owner & Admin Ledger)
  const [roomBonuses, setRoomBonuses] = useState([]);
  const [bonusesLoading, setBonusesLoading] = useState(false);
  const [bonusSearch, setBonusSearch] = useState('');

  // Room Level Modal State
  const [editingRoom, setEditingRoom] = useState(null);
  const [selectedLevelType, setSelectedLevelType] = useState('auto'); // 'auto' or 'manual'
  const [selectedManualLevel, setSelectedManualLevel] = useState(1);
  const [levelUpdating, setLevelUpdating] = useState(false);

  // Room Level Configuration State (L1 - L12+)
  const [levelConfigs, setLevelConfigs] = useState([]);
  const [configsLoading, setConfigsLoading] = useState(false);
  const [configsSaving, setConfigsSaving] = useState(false);

  // Toast State
  const [toast, setToast] = useState({ message: '', type: '', visible: false });
  const showToast = (message, type = 'success') => {
    setToast({ message, type, visible: true });
    setTimeout(() => setToast({ message: '', type: '', visible: false }), 4000);
  };

  useEffect(() => {
    fetchLevelConfigs();
  }, []);

  useEffect(() => {
    if (currentTab === 'rooms') {
      fetchSessions();
      const interval = setInterval(() => {
        fetchSessions(true);
      }, 15000);
      return () => clearInterval(interval);
    } else if (currentTab === 'level_configs') {
      fetchLevelConfigs();
    } else if (currentTab === 'room_bonuses') {
      fetchRoomBonuses();
    }
  }, [activeFilter, currentTab]);

  const fetchRoomBonuses = async () => {
    setBonusesLoading(true);
    try {
      const res = await roomsAPI.getRoomBonuses();
      if (res.data?.success) {
        setRoomBonuses(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching room bonuses:", err);
      showToast("Error loading room bonuses: " + (err.response?.data?.message || err.message), "error");
    } finally {
      setBonusesLoading(false);
    }
  };

  // --------------------------------------------------------------------------
  // 1. Fetch Rooms & Monitoring
  // --------------------------------------------------------------------------
  const fetchSessions = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = activeFilter === 'ALL' 
          ? await roomsAPI.getAllRooms() 
          : await roomsAPI.getActiveRooms();
          
      if (res.data && res.data.success) {
        setSessions(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching sessions:", err);
      if (!silent) showToast("Error loading rooms: " + (err.response?.data?.message || err.message), "error");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleEndSession = async (id) => {
    if (window.confirm("Are you sure you want to forcibly terminate this live room?")) {
      try {
        const res = await roomsAPI.endRoom(id);
        if (res.data.success) {
          showToast("Live session terminated successfully");
          fetchSessions();
        }
      } catch (err) {
        showToast("Failed to end stream session: " + (err.response?.data?.message || err.message), "error");
      }
    }
  };

  // --------------------------------------------------------------------------
  // 2. Room Manual Level Editing
  // --------------------------------------------------------------------------
  const openEditRoomLevelModal = (room) => {
    setEditingRoom(room);
    if (room.manual_level && Number(room.manual_level) > 0) {
      setSelectedLevelType('manual');
      setSelectedManualLevel(Number(room.manual_level));
    } else {
      setSelectedLevelType('auto');
      setSelectedManualLevel(Number(room.effective_level || room.level || 1));
    }
  };

  const handleSaveRoomLevel = async (e) => {
    e.preventDefault();
    if (!editingRoom) return;
    setLevelUpdating(true);
    try {
      const manualVal = selectedLevelType === 'manual' ? selectedManualLevel : null;
      const res = await roomsAPI.updateRoomLevel(editingRoom.id, {
        manual_level: manualVal,
        level: manualVal || 1
      });

      showToast(res.data?.message || "Room level updated successfully!");
      setEditingRoom(null);
      fetchSessions();
    } catch (err) {
      showToast("Error updating room level: " + (err.response?.data?.message || err.message), "error");
    } finally {
      setLevelUpdating(false);
    }
  };

  // --------------------------------------------------------------------------
  // 3. Room Level Rules / Configuration (L1 to L12)
  // --------------------------------------------------------------------------
  const defaultL1toL12 = [
    { level: 1, required_sending: 0, add_admins: 5, add_members: 100, max_admins: 5, max_members: 100, title: 'Level 1' },
    { level: 2, required_sending: 100000, add_admins: 0, add_members: 50, max_admins: 5, max_members: 150, title: 'Level 2' },
    { level: 3, required_sending: 300000, add_admins: 1, add_members: 50, max_admins: 6, max_members: 200, title: 'Level 3' },
    { level: 4, required_sending: 600000, add_admins: 0, add_members: 50, max_admins: 6, max_members: 250, title: 'Level 4' },
    { level: 5, required_sending: 1000000, add_admins: 1, add_members: 50, max_admins: 7, max_members: 300, title: 'Level 5' },
    { level: 6, required_sending: 1500000, add_admins: 0, add_members: 50, max_admins: 7, max_members: 350, title: 'Level 6' },
    { level: 7, required_sending: 2500000, add_admins: 1, add_members: 50, max_admins: 8, max_members: 400, title: 'Level 7' },
    { level: 8, required_sending: 4000000, add_admins: 0, add_members: 50, max_admins: 8, max_members: 450, title: 'Level 8' },
    { level: 9, required_sending: 6000000, add_admins: 1, add_members: 50, max_admins: 9, max_members: 500, title: 'Level 9' },
    { level: 10, required_sending: 10000000, add_admins: 1, add_members: 100, max_admins: 10, max_members: 600, title: 'Level 10' },
    { level: 11, required_sending: 15000000, add_admins: 1, add_members: 100, max_admins: 11, max_members: 700, title: 'Level 11' },
    { level: 12, required_sending: 20000000, add_admins: 1, add_members: 100, max_admins: 12, max_members: 800, title: 'Level 12' }
  ];

  const fetchLevelConfigs = async () => {
    setConfigsLoading(true);
    try {
      const res = await roomsAPI.getLevelConfigs();
      if (res.data && res.data.success && Array.isArray(res.data.data) && res.data.data.length > 0) {
        setLevelConfigs(res.data.data);
      } else {
        setLevelConfigs(defaultL1toL12);
      }
    } catch (err) {
      console.error("Error fetching room level configs:", err);
      setLevelConfigs(defaultL1toL12);
    } finally {
      setConfigsLoading(false);
    }
  };

  // Manual Configuration Update (Direct without auto-increment calculation)
  const handleConfigChange = (index, field, value) => {
    const updated = [...levelConfigs];
    if (field === 'title') {
      updated[index][field] = value;
    } else if (field === 'level') {
      updated[index][field] = Math.max(1, parseInt(value) || 1);
    } else {
      updated[index][field] = Math.max(0, parseInt(value) || 0);
    }
    setLevelConfigs(updated);
  };

  // Add New Custom Level
  const handleAddLevel = () => {
    const maxLvl = levelConfigs.reduce((max, c) => Math.max(max, Number(c.level) || 0), 0);
    const nextLvl = maxLvl + 1;
    const newRow = {
      level: nextLvl,
      title: `Level ${nextLvl}`,
      required_sending: (nextLvl - 1) * 200000,
      max_admins: 5,
      max_members: 100,
      add_admins: 0,
      add_members: 0
    };
    setLevelConfigs([...levelConfigs, newRow]);
    showToast(`Added Level ${nextLvl}. Set custom label, sending threshold & limits, then click Save.`);
  };

  // Delete Level
  const handleDeleteLevel = (index) => {
    const row = levelConfigs[index];
    if (window.confirm(`Are you sure you want to delete ${row.title || 'Level ' + row.level}?`)) {
      const updated = levelConfigs.filter((_, i) => i !== index);
      setLevelConfigs(updated);
      showToast(`Removed ${row.title || 'Level ' + row.level}. Click Save Configurations to persist.`);
    }
  };

  const handleSaveConfigs = async () => {
    setConfigsSaving(true);
    try {
      const res = await roomsAPI.saveLevelConfigs(levelConfigs, true);
      showToast(res.data?.message || "Room Level Configurations saved successfully!");
      if (res.data?.data) {
        setLevelConfigs(res.data.data);
      }
    } catch (err) {
      showToast("Failed to save level configs: " + (err.response?.data?.message || err.message), "error");
    } finally {
      setConfigsSaving(false);
    }
  };

  const handleResetDefaultConfigs = () => {
    if (window.confirm("Reset all room levels to the default L1-L12 table?")) {
      setLevelConfigs(defaultL1toL12);
      showToast("Reset to default table. Click 'Save Configurations' to apply.");
    }
  };

  const getDuration = (startedAt, endedAt) => {
    if (!startedAt) return '00:00';
    const start = new Date(startedAt);
    const end = endedAt ? new Date(endedAt) : new Date();
    const diffMs = end.getTime() - start.getTime();
    if (diffMs < 0) return '00:00';
    const diffSecs = Math.floor(diffMs / 1000);
    const hours = Math.floor(diffSecs / 3600);
    const mins = Math.floor((diffSecs % 3600) / 60);
    const secs = diffSecs % 60;
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      
      {/* Top Header & Tab Navigation */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 bg-white p-6 rounded-3xl border border-slate-100 shadow-xs">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight">Room Management</h2>
            <span className="px-3 py-0.5 rounded-full text-xs font-black bg-purple-100 text-purple-700">
              Live & Levels
            </span>
          </div>
          <p className="text-slate-500 mt-1 text-sm">
            Monitor live streams, configure Room Levels (L1-L12), and manage admin and member capacities.
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex items-center gap-2 bg-slate-100 p-1.5 rounded-2xl w-full sm:w-auto">
          <button
            onClick={() => setCurrentTab('rooms')}
            className={`flex-1 sm:flex-initial px-5 py-2.5 rounded-xl text-xs font-extrabold transition flex items-center justify-center gap-2 cursor-pointer ${
              currentTab === 'rooms'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Play size={15} className={currentTab === 'rooms' ? 'text-pink-600' : ''} />
            Live & All Rooms
          </button>
          <button
            onClick={() => setCurrentTab('level_configs')}
            className={`flex-1 sm:flex-initial px-5 py-2.5 rounded-xl text-xs font-extrabold transition flex items-center justify-center gap-2 cursor-pointer ${
              currentTab === 'level_configs'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Layers size={15} className={currentTab === 'level_configs' ? 'text-purple-600' : ''} />
            Room Level Rules (L1 - L12)
          </button>
          <button
            onClick={() => setCurrentTab('room_bonuses')}
            className={`flex-1 sm:flex-initial px-5 py-2.5 rounded-xl text-xs font-extrabold transition flex items-center justify-center gap-2 cursor-pointer ${
              currentTab === 'room_bonuses'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <Sparkles size={15} className={currentTab === 'room_bonuses' ? 'text-amber-500' : ''} />
            Room Bonus Ledger
          </button>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* TAB 1: LIVE & ALL ROOMS MONITORING */}
      {/* ========================================================================= */}
      {currentTab === 'rooms' && (
        <div className="space-y-6">
          
          {/* Subheader controls */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div className="flex items-center gap-2 bg-white p-1 rounded-xl border border-slate-200 shadow-2xs">
              <button 
                onClick={() => setActiveFilter('ACTIVE')}
                className={`px-4 py-1.5 rounded-lg text-xs font-black transition cursor-pointer ${
                  activeFilter === 'ACTIVE' ? 'bg-pink-600 text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                🔴 Active Streams ({sessions.filter(s => s.is_active).length})
              </button>
              <button 
                onClick={() => setActiveFilter('ALL')}
                className={`px-4 py-1.5 rounded-lg text-xs font-black transition cursor-pointer ${
                  activeFilter === 'ALL' ? 'bg-slate-900 text-white shadow-xs' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                All Rooms ({sessions.length})
              </button>
            </div>

            <button 
              onClick={() => fetchSessions()}
              className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition shadow-2xs active:scale-95 text-xs font-bold flex items-center gap-2 cursor-pointer"
              title="Refresh list"
            >
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>

          {/* Rooms Grid */}
          {loading ? (
            <div className="p-20 text-center bg-white rounded-3xl border border-slate-100 shadow-sm">
              <div className="animate-spin w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-slate-600 font-bold">Loading rooms & capacities...</p>
            </div>
          ) : sessions.length === 0 ? (
            <div className="p-20 text-center bg-white rounded-3xl border border-slate-100 shadow-sm text-slate-400">
              <Play size={48} className="mx-auto mb-4 opacity-30" />
              <p className="font-bold text-lg text-slate-700">No rooms found.</p>
              <p className="text-xs text-slate-400 mt-1">There are no {activeFilter === 'ACTIVE' ? 'active' : 'recorded'} rooms on the server.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {sessions.map((session) => {
                const isLive = Boolean(session.is_active);
                const seed = session.owner_name ? session.owner_name.charCodeAt(0) % 20 : 1;
                const roomLevel = session.effective_level || session.level || 1;
                const isManual = Boolean(session.is_manual_level);
                const maxAdmins = session.max_admins || 5;
                const maxMembers = session.max_members || 100;
                const currentAdmins = session.admin_count || 0;
                const currentMembers = session.active_count || 0;
                
                return (
                  <div key={session.id} className="group relative bg-white rounded-[26px] overflow-hidden shadow-xs hover:shadow-xl transition-all duration-300 border border-slate-100 flex flex-col justify-between">
                    
                    {/* Visual Thumbnail Area */}
                    <div className="relative h-44 bg-slate-900 overflow-hidden shrink-0">
                      <img 
                        src={session.room_avatar || session.room_theme || session.avatar_url || 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=500&auto=format&fit=crop&q=60'} 
                        alt="Room DP" 
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=500&auto=format&fit=crop&q=60';
                        }}
                        className="w-full h-full object-cover opacity-80 group-hover:opacity-60 transition-all duration-300" 
                      />
                      
                      {/* Top Badges */}
                      <div className="absolute top-3 left-3 flex items-center gap-1.5">
                        <div className="bg-black/70 backdrop-blur-md text-white text-xs px-2.5 py-1 rounded-xl flex items-center gap-1.5 font-black">
                          <Users size={12} className="text-pink-400" /> {currentMembers} / {maxMembers}
                        </div>
                      </div>
                      
                      <div className="absolute top-3 right-3 flex items-center gap-1.5">
                        <div className={`text-white text-[10px] px-2.5 py-1 rounded-xl font-black uppercase tracking-wider ${
                          isLive ? 'bg-red-500 animate-pulse' : 'bg-slate-700'
                        }`}>
                          {isLive ? 'LIVE' : 'ENDED'}
                        </div>
                      </div>
                      
                      <div className="absolute bottom-3 left-3 bg-black/70 backdrop-blur-md text-white text-[11px] px-2.5 py-0.5 rounded-lg flex items-center gap-1 font-bold">
                        <Clock size={11} /> {getDuration(session.created_at, session.updated_at)}
                      </div>

                      {/* Force End Action (Hover Overlay) */}
                      {isLive && (
                        <div className="absolute inset-0 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-10 bg-slate-950/30 backdrop-blur-xs">
                          <button 
                            onClick={() => handleEndSession(session.id)}
                            className="bg-red-600 text-white px-4 py-2 rounded-xl text-xs font-black hover:bg-red-700 shadow-lg transform hover:scale-105 transition flex items-center gap-1.5 cursor-pointer"
                            title="Force End Stream"
                          >
                            <XCircle size={15} /> End Session
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Room Details */}
                    <div className="p-5 flex-1 flex flex-col justify-between space-y-4">
                      
                      {/* Title & Host info */}
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-full p-[2px] bg-gradient-to-tr from-pink-500 to-purple-600 shrink-0">
                          <img 
                            src={session.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(session.owner_name || 'Owner')}`} 
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(session.owner_name || 'Owner')}`;
                            }}
                            className="w-full h-full rounded-full border-2 border-white object-cover" 
                            alt="" 
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="font-black text-slate-800 text-sm truncate">{session.name || 'Room'}</h4>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="text-[11px] font-bold text-slate-600 truncate">Owner: {session.owner_name}</p>
                            {session.country && <CountryBadge country={session.country} className="text-[10px] px-1.5 py-0.5" />}
                          </div>

                          {/* Owner Role Tag Badge */}
                          <div className="flex items-center gap-1 mt-1 flex-wrap">
                            {session.owner_is_host && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-black bg-pink-100 text-pink-700 border border-pink-200">
                                🎤 HOST
                              </span>
                            )}
                            {session.owner_is_agency && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-black bg-purple-100 text-purple-700 border border-purple-200">
                                🏢 AGENCY
                              </span>
                            )}
                            {session.owner_is_seller && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-black bg-amber-100 text-amber-700 border border-amber-200">
                                💰 SELLER
                              </span>
                            )}
                            {!session.owner_is_host && !session.owner_is_agency && !session.owner_is_seller && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-black bg-slate-100 text-slate-600 border border-slate-200">
                                👤 {session.owner_user_type || 'NORMAL USER'}
                              </span>
                            )}
                          </div>

                          <p className="text-[10px] font-mono text-purple-600 font-bold truncate mt-0.5">ID: {session.owner_xoxo_id || 'N/A'}</p>
                        </div>
                      </div>

                      {/* Room Level & Capacity Badges */}
                      <div className="space-y-2 p-3 bg-slate-50 rounded-2xl border border-slate-100">
                        
                        {/* Level Info */}
                        <div className="flex justify-between items-center text-xs">
                          <span className="font-extrabold text-slate-500 text-[11px]">Room Level:</span>
                          <span className={`px-2.5 py-0.5 rounded-full font-black text-xs border ${
                            isManual 
                              ? 'bg-purple-100 text-purple-800 border-purple-200' 
                              : 'bg-amber-100 text-amber-800 border-amber-200'
                          }`}>
                            ⭐ L{roomLevel} ({isManual ? 'Manual' : 'Auto'})
                          </span>
                        </div>

                        {/* Capacity Stats */}
                        <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-200/60 text-[11px]">
                          <div className="flex flex-col">
                            <span className="text-slate-400 font-bold text-[10px]">Admins:</span>
                            <span className={`font-black ${currentAdmins >= maxAdmins ? 'text-rose-600' : 'text-slate-800'}`}>
                              🛡️ {currentAdmins} / {maxAdmins}
                            </span>
                          </div>
                          <div className="flex flex-col">
                            <span className="text-slate-400 font-bold text-[10px]">Members:</span>
                            <span className={`font-black ${currentMembers >= maxMembers ? 'text-rose-600' : 'text-slate-800'}`}>
                              👥 {currentMembers} / {maxMembers}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Edit Room Level Action */}
                      <button
                        onClick={() => openEditRoomLevelModal(session)}
                        className="w-full py-2 bg-gradient-to-r from-purple-50 to-indigo-50 hover:from-purple-100 hover:to-indigo-100 text-purple-700 border border-purple-200/80 rounded-xl text-xs font-black transition flex items-center justify-center gap-1.5 cursor-pointer shadow-2xs active:scale-95"
                      >
                        <Edit3 size={13} /> Edit Room Level
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 2: ROOM LEVEL RULES / CONFIGURATION (MANUAL CONFIG & ADD LEVELS) */}
      {/* ========================================================================= */}
      {currentTab === 'level_configs' && (
        <div className="bg-white p-6 sm:p-8 rounded-3xl border border-slate-100 shadow-sm space-y-6">
          
          {/* Rules header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-6 border-b border-slate-100">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-2xl font-black text-slate-900">ROOM LEVEL CONFIGURATION & CAPACITIES</h3>
                <span className="px-3 py-0.5 rounded-full text-xs font-black bg-purple-100 text-purple-800">
                  {levelConfigs.length} Levels Configured
                </span>
              </div>
              <p className="text-slate-500 text-xs font-medium mt-1">
                Admin can create new levels, customize labels, and directly specify max admin and max member capacity for each level manually.
              </p>
            </div>

            <div className="flex items-center gap-2.5 flex-wrap">
              <button
                type="button"
                onClick={handleAddLevel}
                className="px-4 py-2.5 bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200 rounded-xl text-xs font-black transition flex items-center gap-1.5 cursor-pointer shadow-2xs active:scale-95"
              >
                <Plus size={15} /> Add New Level
              </button>
              <button
                type="button"
                onClick={handleResetDefaultConfigs}
                className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition cursor-pointer"
              >
                Reset Default (L1-L12)
              </button>
              <button
                type="button"
                onClick={handleSaveConfigs}
                disabled={configsSaving}
                className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl text-xs font-black transition shadow-md flex items-center gap-2 cursor-pointer disabled:opacity-50 active:scale-95"
              >
                {configsSaving ? <RefreshCw size={15} className="animate-spin" /> : <Save size={15} />}
                Save Configurations
              </button>
            </div>
          </div>

          {/* Configs Table */}
          {configsLoading ? (
            <div className="p-16 text-center">
              <div className="animate-spin w-8 h-8 border-3 border-purple-500 border-t-transparent rounded-full mx-auto mb-3"></div>
              <p className="text-slate-500 text-xs font-bold">Loading level configurations...</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="overflow-x-auto rounded-2xl border border-slate-200/80">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 text-slate-500 text-xs font-black uppercase tracking-wider border-b border-slate-200">
                      <th className="p-4 w-16 text-center">S.No.</th>
                      <th className="p-4 w-28">Level #</th>
                      <th className="p-4 min-w-[200px]">Level Label / Name</th>
                      <th className="p-4 w-56">Required Sending (Coins)</th>
                      <th className="p-4 w-48">Max Admins Allowed</th>
                      <th className="p-4 w-48">Max Members Allowed</th>
                      <th className="p-4 w-24 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-sm">
                    {levelConfigs.map((cfg, idx) => (
                      <tr key={cfg.level || idx} className="hover:bg-purple-50/20 transition">
                        <td className="p-4 text-center font-bold text-slate-400">
                          {idx + 1}
                        </td>
                        <td className="p-4">
                          <span className="px-3 py-1 rounded-xl text-xs font-black bg-purple-100 text-purple-900 border border-purple-200">
                            Level {cfg.level}
                          </span>
                        </td>
                        <td className="p-4">
                          <input
                            type="text"
                            value={cfg.title !== undefined ? cfg.title : `Level ${cfg.level}`}
                            onChange={(e) => handleConfigChange(idx, 'title', e.target.value)}
                            placeholder="e.g. Level 1, VIP Diamond"
                            className="w-full max-w-sm px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:bg-white transition"
                          />
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-1.5">
                            <span className="text-base">🎁</span>
                            <input
                              type="number"
                              min="0"
                              step="10000"
                              value={cfg.required_sending !== undefined ? cfg.required_sending : 0}
                              onChange={(e) => handleConfigChange(idx, 'required_sending', e.target.value)}
                              placeholder="0"
                              className="w-36 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-black text-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:bg-white transition"
                            />
                            <span className="text-xs text-amber-600 font-bold">coins</span>
                          </div>
                          <p className="text-[10px] text-slate-400 font-semibold mt-0.5 pl-6">
                            {cfg.level === 1 ? 'Baseline (0 coins)' : `${Number(cfg.required_sending || 0).toLocaleString()} coins sending`}
                          </p>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <span className="text-base">🛡️</span>
                            <input
                              type="number"
                              min="0"
                              value={cfg.max_admins !== undefined ? cfg.max_admins : 5}
                              onChange={(e) => handleConfigChange(idx, 'max_admins', e.target.value)}
                              className="w-28 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-black text-slate-800 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:bg-white transition"
                            />
                            <span className="text-xs text-slate-400 font-bold">admins</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <span className="text-base">👥</span>
                            <input
                              type="number"
                              min="0"
                              step="50"
                              value={cfg.max_members !== undefined ? cfg.max_members : 100}
                              onChange={(e) => handleConfigChange(idx, 'max_members', e.target.value)}
                              className="w-32 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-black text-slate-800 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:bg-white transition"
                            />
                            <span className="text-xs text-slate-400 font-bold">users</span>
                          </div>
                        </td>
                        <td className="p-4 text-center">
                          <button
                            type="button"
                            onClick={() => handleDeleteLevel(idx)}
                            className="p-2 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-xl transition cursor-pointer border border-transparent hover:border-rose-200"
                            title="Delete this level"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Bottom Add Level Bar */}
              <div className="flex justify-between items-center pt-2">
                <button
                  type="button"
                  onClick={handleAddLevel}
                  className="px-5 py-2.5 bg-slate-100 hover:bg-purple-50 text-slate-700 hover:text-purple-700 border border-slate-200 hover:border-purple-200 rounded-xl text-xs font-bold transition flex items-center gap-2 cursor-pointer active:scale-95"
                >
                  <Plus size={16} className="text-purple-600" /> Add Another Room Level
                </button>
                <span className="text-xs text-slate-400 font-semibold">
                  Remember to click <strong>"Save Configurations"</strong> after making edits.
                </span>
              </div>
            </div>
          )}

          {/* Bottom Alert Explainer */}
          <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 flex items-start gap-3 text-xs text-slate-600 font-medium">
            <Sparkles size={18} className="text-purple-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-extrabold text-slate-900 mb-0.5">Manual Room Level Configuration:</p>
              Admins have complete manual control over room levels. You can create as many levels as needed, assign custom labels, and define the exact maximum admins and max members allowed in each room level.
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* TAB 3: ROOM BONUS LEDGER (OWNER & ADMIN VIEW) */}
      {/* ========================================================================= */}
      {currentTab === 'room_bonuses' && (
        <div className="space-y-6">
          {/* Top KPI Cards for Room Bonuses */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm">
              <div className="flex items-center justify-between text-slate-400 text-xs font-bold uppercase">
                <span>Rooms with Bonus</span>
                <div className="p-2 bg-amber-50 text-amber-600 rounded-lg">
                  <Sparkles size={16} />
                </div>
              </div>
              <p className="text-2xl font-black text-slate-800 mt-2">{roomBonuses.length}</p>
              <p className="text-[11px] text-slate-400 font-medium mt-1">Active Bonus Generating Rooms</p>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm">
              <div className="flex items-center justify-between text-slate-400 text-xs font-bold uppercase">
                <span>Daily Contribution</span>
                <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                  <Coins size={16} />
                </div>
              </div>
              <p className="text-2xl font-black text-blue-600 mt-2">
                🪙 {roomBonuses.reduce((acc, r) => acc + Number(r.daily_contribution || 0), 0).toLocaleString()}
              </p>
              <p className="text-[11px] text-slate-400 font-medium mt-1">Total Daily Coin Contributions</p>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm">
              <div className="flex items-center justify-between text-slate-400 text-xs font-bold uppercase">
                <span>Claimable Reward</span>
                <div className="p-2 bg-green-50 text-green-600 rounded-lg">
                  <Gift size={16} />
                </div>
              </div>
              <p className="text-2xl font-black text-green-600 mt-2">
                🪙 {roomBonuses.reduce((acc, r) => acc + Number(r.claimable_reward || 0), 0).toLocaleString()}
              </p>
              <p className="text-[11px] text-slate-400 font-medium mt-1">Unclaimed Host Room Rewards</p>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm">
              <div className="flex items-center justify-between text-slate-400 text-xs font-bold uppercase">
                <span>Total Bonus Ledger</span>
                <div className="p-2 bg-pink-50 text-pink-600 rounded-lg">
                  <Award size={16} />
                </div>
              </div>
              <p className="text-2xl font-black text-pink-600 mt-2">
                🪙 {roomBonuses.reduce((acc, r) => acc + Number(r.total_bonus_coins || 0), 0).toLocaleString()}
              </p>
              <p className="text-[11px] text-slate-400 font-medium mt-1">Sum of All Room Bonuses</p>
            </div>
          </div>

          {/* Search & Actions Bar */}
          <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="relative w-full sm:w-96">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                placeholder="Search by Room Name, ID, Owner, or Role..."
                value={bonusSearch}
                onChange={(e) => setBonusSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-500/20 transition"
              />
            </div>
            <button
              onClick={fetchRoomBonuses}
              className="px-4 py-2.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 flex items-center gap-2 cursor-pointer transition active:scale-95"
            >
              <RefreshCw size={14} className={bonusesLoading ? "animate-spin" : ""} /> Refresh Bonus Data
            </button>
          </div>

          {/* Table */}
          <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
            {bonusesLoading ? (
              <div className="p-16 text-center text-slate-400">
                <RefreshCw size={32} className="animate-spin mx-auto mb-3 text-pink-500" />
                <p className="text-sm font-semibold">Loading Room Bonus Ledger...</p>
              </div>
            ) : roomBonuses.filter((b) => {
                if (!bonusSearch) return true;
                const s = bonusSearch.toLowerCase();
                return (
                  (b.room_name && b.room_name.toLowerCase().includes(s)) ||
                  (b.display_room_id && b.display_room_id.toLowerCase().includes(s)) ||
                  (b.owner_name && b.owner_name.toLowerCase().includes(s)) ||
                  (b.owner_xoxo_id && b.owner_xoxo_id.toLowerCase().includes(s)) ||
                  (b.owner_user_type && b.owner_user_type.toLowerCase().includes(s))
                );
              }).length === 0 ? (
              <div className="p-16 text-center text-slate-400">
                <Sparkles size={40} className="mx-auto mb-3 opacity-30 text-amber-500" />
                <p className="text-base font-bold text-slate-700">No Room Bonus Records Found</p>
                <p className="text-xs text-slate-400 mt-1">When users interact and send gifts in rooms, bonuses accumulate here for the Owner to audit.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50/80 text-slate-400 text-xs font-bold uppercase tracking-wider border-b border-slate-100">
                      <th className="p-5">Room Information</th>
                      <th className="p-5">Room Owner & Role</th>
                      <th className="p-5">Daily Contribution</th>
                      <th className="p-5">Claimable Reward</th>
                      <th className="p-5">Next Week Coins</th>
                      <th className="p-5 font-black text-pink-600">Total Bonus Coins</th>
                      <th className="p-5">Cycle Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-sm">
                    {roomBonuses
                      .filter((b) => {
                        if (!bonusSearch) return true;
                        const s = bonusSearch.toLowerCase();
                        return (
                          (b.room_name && b.room_name.toLowerCase().includes(s)) ||
                          (b.display_room_id && b.display_room_id.toLowerCase().includes(s)) ||
                          (b.owner_name && b.owner_name.toLowerCase().includes(s)) ||
                          (b.owner_xoxo_id && b.owner_xoxo_id.toLowerCase().includes(s)) ||
                          (b.owner_user_type && b.owner_user_type.toLowerCase().includes(s))
                        );
                      })
                      .map((item) => (
                        <tr key={item.bonus_id} className="hover:bg-slate-50/50 transition">
                          <td className="p-5">
                            <div className="flex items-center gap-3">
                              <img
                                src={item.room_avatar || item.room_theme || 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=500&auto=format&fit=crop&q=60'}
                                alt="Room DP"
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src = 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=500&auto=format&fit=crop&q=60';
                                }}
                                className="w-12 h-12 rounded-2xl object-cover border border-slate-200 shrink-0"
                              />
                              <div>
                                <p className="font-black text-slate-800 text-sm">{item.room_name}</p>
                                <p className="text-[11px] font-mono text-purple-600 font-bold">ID: {item.display_room_id}</p>
                                <span className={`inline-block px-2 py-0.5 rounded-full text-[9px] font-black uppercase mt-1 ${
                                  item.is_room_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                                }`}>
                                  {item.is_room_active ? '● Live Now' : 'Offline'}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="p-5">
                            <div className="flex items-center gap-2.5">
                              <img
                                src={item.owner_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(item.owner_name || 'Owner')}`}
                                alt=""
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(item.owner_name || 'Owner')}`;
                                }}
                                className="w-9 h-9 rounded-full object-cover border border-slate-200 shrink-0"
                              />
                              <div>
                                <div className="flex items-center gap-1.5">
                                  <p className="font-bold text-slate-800 text-sm">{item.owner_name}</p>
                                  {item.owner_country && <CountryBadge country={item.owner_country} className="text-[9px] px-1 py-0.2" />}
                                </div>
                                <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                                  {item.owner_is_host && (
                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-black bg-pink-100 text-pink-700 border border-pink-200">
                                      🎤 HOST
                                    </span>
                                  )}
                                  {item.owner_is_agency && (
                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-black bg-purple-100 text-purple-700 border border-purple-200">
                                      🏢 AGENCY
                                    </span>
                                  )}
                                  {item.owner_is_seller && (
                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-black bg-amber-100 text-amber-700 border border-amber-200">
                                      💰 SELLER
                                    </span>
                                  )}
                                  {!item.owner_is_host && !item.owner_is_agency && !item.owner_is_seller && (
                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-black bg-slate-100 text-slate-600 border border-slate-200">
                                      👤 {item.owner_user_type || 'NORMAL USER'}
                                    </span>
                                  )}
                                </div>
                                <p className="text-[10px] font-mono text-slate-400 mt-0.5">XOXO: {item.owner_xoxo_id}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-5 font-bold text-blue-600">
                            🪙 {Number(item.daily_contribution || 0).toLocaleString()}
                          </td>
                          <td className="p-5 font-bold text-green-600">
                            🪙 {Number(item.claimable_reward || 0).toLocaleString()}
                          </td>
                          <td className="p-5 font-bold text-slate-600">
                            🪙 {Number(item.next_week_coins || 0).toLocaleString()}
                          </td>
                          <td className="p-5 font-black text-pink-600 text-base">
                            🪙 {Number(item.total_bonus_coins || 0).toLocaleString()}
                          </td>
                          <td className="p-5 text-xs text-slate-400 font-medium">
                            {item.cycle_date || (item.updated_at ? new Date(item.updated_at).toLocaleDateString() : 'Active Cycle')}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL: EDIT ROOM LEVEL (AUTO VS MANUAL) */}
      {/* ========================================================================= */}
      {editingRoom && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-[28px] w-full max-w-lg overflow-hidden shadow-2xl border border-slate-100 flex flex-col">
            
            {/* Modal Header */}
            <div className="p-6 bg-gradient-to-r from-purple-700 via-indigo-700 to-pink-600 text-white flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-white/20 rounded-2xl backdrop-blur-md">
                  <Crown size={22} className="text-amber-300" />
                </div>
                <div>
                  <h3 className="font-extrabold text-lg">Edit Room Level</h3>
                  <p className="text-purple-200 text-xs font-medium">
                    Configure room level mode (Auto vs Manual)
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setEditingRoom(null)}
                className="p-2 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSaveRoomLevel} className="p-6 space-y-5">
              
              {/* Room Snapshot */}
              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 flex items-center justify-between">
                <div>
                  <h4 className="font-black text-slate-800 text-sm">{editingRoom.name || 'Room'}</h4>
                  <p className="text-xs text-slate-500">Host: {editingRoom.owner_name}</p>
                </div>
                <div className="text-right">
                  <span className="text-xs font-black text-purple-700 bg-purple-100 px-3 py-1 rounded-full">
                    Current: L{editingRoom.effective_level || editingRoom.level || 1}
                  </span>
                </div>
              </div>

              {/* Mode Selector: Auto vs Manual */}
              <div className="space-y-3">
                <label className="text-xs font-extrabold text-slate-700 uppercase tracking-wide">Level Mode</label>
                
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setSelectedLevelType('auto')}
                    className={`p-3.5 rounded-2xl border text-left transition cursor-pointer flex flex-col justify-between ${
                      selectedLevelType === 'auto'
                        ? 'bg-amber-50/70 border-amber-400 text-amber-900 ring-2 ring-amber-400/20 shadow-xs'
                        : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center justify-between w-full">
                      <span className="font-black text-xs">🤖 Auto (Calculated)</span>
                      {selectedLevelType === 'auto' && <CheckCircle size={14} className="text-amber-600" />}
                    </div>
                    <p className="text-[10px] text-slate-500 mt-1">Calculates dynamically based on room activity & rocket level.</p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setSelectedLevelType('manual')}
                    className={`p-3.5 rounded-2xl border text-left transition cursor-pointer flex flex-col justify-between ${
                      selectedLevelType === 'manual'
                        ? 'bg-purple-50/70 border-purple-400 text-purple-900 ring-2 ring-purple-400/20 shadow-xs'
                        : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center justify-between w-full">
                      <span className="font-black text-xs">⚙️ Manual Level</span>
                      {selectedLevelType === 'manual' && <CheckCircle size={14} className="text-purple-600" />}
                    </div>
                    <p className="text-[10px] text-slate-500 mt-1">Superadmin explicitly fixes the room level.</p>
                  </button>
                </div>
              </div>

              {/* Level Selector Dropdown (when Manual is selected) */}
              {selectedLevelType === 'manual' && (
                <div className="p-4 bg-purple-50/50 rounded-2xl border border-purple-200 space-y-3 animate-in fade-in duration-200">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-black text-purple-950">Select Fixed Room Level:</label>
                    <span className="text-xs font-extrabold text-purple-700 bg-purple-100 px-2.5 py-0.5 rounded-lg">
                      {levelConfigs.find(c => Number(c.level) === selectedManualLevel)?.title || `Level ${selectedManualLevel}`}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-56 overflow-y-auto p-1">
                    {(levelConfigs && levelConfigs.length > 0 ? levelConfigs : defaultL1toL12).map((cfg) => {
                      const lvlNum = Number(cfg.level);
                      const isSelected = selectedManualLevel === lvlNum;
                      return (
                        <button
                          key={lvlNum}
                          type="button"
                          onClick={() => setSelectedManualLevel(lvlNum)}
                          className={`p-2.5 rounded-2xl text-xs font-black transition cursor-pointer flex flex-col items-center justify-center gap-0.5 border ${
                            isSelected
                              ? 'bg-purple-600 text-white border-purple-600 shadow-sm ring-2 ring-purple-400/40'
                              : 'bg-white text-slate-700 border-slate-200 hover:bg-purple-50'
                          }`}
                        >
                          <span className="truncate w-full text-center">{cfg.title || `L${lvlNum}`}</span>
                          <span className={`text-[10px] font-bold ${isSelected ? 'text-purple-200' : 'text-slate-400'}`}>
                            🛡️ {cfg.max_admins} | 👥 {cfg.max_members}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Level stats preview */}
                  {levelConfigs.find(c => Number(c.level) === selectedManualLevel) && (
                    <div className="pt-2 text-[11px] text-purple-900 font-bold flex justify-between border-t border-purple-200/60">
                      <span>Max Admins Allowed: 🛡️ {levelConfigs.find(c => Number(c.level) === selectedManualLevel).max_admins}</span>
                      <span>Max Members Allowed: 👥 {levelConfigs.find(c => Number(c.level) === selectedManualLevel).max_members}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Modal Footer */}
              <div className="pt-2 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setEditingRoom(null)}
                  className="px-5 py-2.5 border border-slate-300 hover:bg-slate-100 text-slate-700 rounded-xl text-xs font-bold transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={levelUpdating}
                  className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl text-xs font-black transition flex items-center gap-2 shadow-md cursor-pointer disabled:opacity-50"
                >
                  {levelUpdating ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
                  Save Room Level
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.visible && (
        <div className={`fixed bottom-4 right-4 z-[9999] px-6 py-3 rounded-xl shadow-lg border text-sm font-bold flex items-center gap-2 animate-in slide-in-from-bottom-5 fade-in duration-300 ${
          toast.type === 'error' ? 'bg-red-50 text-red-600 border-red-100' : 'bg-green-50 text-green-700 border-green-100'
        }`}>
          {toast.type === 'error' ? <AlertTriangle size={18} /> : <CheckCircle size={18} />}
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default LiveSessions;